import { STATES } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import { useCheckIsUpdatePeriod } from '../../../../components/update-period-announcement-panel';
import { useUser } from '../../../../user-management';
import { useClassroomCapabilities } from '../../../hooks/use-classroom-capabilities';
import {
  isClassroomCreatedManually,
  isJourneyClassroom,
  type Classroom,
} from '../../../modules/classroom';
import type { ClassCardOptions } from '../../class-card/class-card.options';

type Props = {
  classroom: Classroom;
  openEditModal: (classroom: Classroom) => void;
  onLeaveClassWithConfirmation: (classroom: Classroom) => void;
  onDeleteClassWithConfirmation: (classroom: Classroom) => void;
};

function useClassroomCreateOptions({
  classroom,
  openEditModal,
  onLeaveClassWithConfirmation,
  onDeleteClassWithConfirmation,
}: Props): { options: ClassCardOptions } {
  const { t } = useTranslation();
  const isUpdatePeriod = useCheckIsUpdatePeriod();
  const { user } = useUser();
  const {
    classroom: { canDelete, canTeacherLeave },
  } = useClassroomCapabilities({ classroom });

  const userId = user?.id ?? '';

  const numberOfStudents = classroom.numberOfStudents || 0;

  const createJourneysOptions = () => {
    const options: ClassCardOptions = {
      edit: {
        text: t('common.edit'),
        onClick: () => {
          openEditModal(classroom);
        },
        state: isUpdatePeriod ? STATES.DISABLED : STATES.ACTIVE,
      },
    };

    options.leave = {
      text: t('my-classes.leave-journey'),
      onClick: () => onLeaveClassWithConfirmation(classroom),
      state: canTeacherLeave(userId) ? STATES.ACTIVE : STATES.DISABLED,
    };

    const hasStudents = numberOfStudents > 0;

    options.delete = {
      text: t('classrooms.delete-journey'),
      subtitle: hasStudents
        ? t('my-classes.delete-journey.warning')
        : undefined,
      onClick: () => onDeleteClassWithConfirmation(classroom),
      state: canDelete ? STATES.ACTIVE : STATES.DISABLED,
    };

    return options;
  };

  const createClassroomOptions = () => {
    const hasStudents = numberOfStudents > 0;
    const isCreatedManually = isClassroomCreatedManually(classroom);

    const options: ClassCardOptions = {
      edit: {
        text: t('common.edit'),
        onClick: () => {
          openEditModal(classroom);
        },
        state: isUpdatePeriod ? STATES.DISABLED : STATES.ACTIVE,
      },
      leave: {
        text: t('teachers.unlinkTeacher-myself'),
        onClick: () => onLeaveClassWithConfirmation(classroom),
        subtitle: !canTeacherLeave(userId)
          ? t('manager.leave-classroom-rostered.warning')
          : undefined,
        state: canTeacherLeave(userId) ? STATES.ACTIVE : STATES.DISABLED,
      },
      delete: {
        text: t('classrooms.deleteClassroom'),
        subtitle: !isCreatedManually
          ? t('manager.delete-classroom-rostered.warning')
          : hasStudents
          ? t('my-classes.delete-classroom.warning')
          : undefined,
        onClick: () => onDeleteClassWithConfirmation(classroom),
        state: canDelete ? STATES.ACTIVE : STATES.DISABLED,
      },
    };

    return options;
  };

  const createOptions = () => {
    return isJourneyClassroom(classroom)
      ? createJourneysOptions()
      : createClassroomOptions();
  };

  return { options: createOptions() };
}

export { useClassroomCreateOptions };
